/**
 * Trading Validation Hub
 * 40-Day Validation Monitoring Dashboard
 *
 * Displays all validation metrics and real-time monitoring for paper trading
 */

import React, { useState } from 'react'
import TradingValidationHome from '@/pages/TradingValidation/TradingValidationHome'
import LiveFeed from '@/pages/TradingValidation/LiveFeed'
import DailyMetrics from '@/pages/TradingValidation/DailyMetrics'
import EnhancementDashboard from '@/pages/TradingValidation/EnhancementDashboard'
import HealthMonitor from '@/pages/TradingValidation/HealthMonitor'
import RiskAnalysis from '@/pages/TradingValidation/RiskAnalysis'
import TradeAnalysis from '@/pages/TradingValidation/TradeAnalysis'
import Reports from '@/pages/TradingValidation/Reports'

interface ValidationTab {
  id: string
  label: string
  component: React.ComponentType
  icon?: string
}

const TradingValidationHub: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('overview')

  const tabs: ValidationTab[] = [
    { id: 'overview', label: 'Overview', component: TradingValidationHome, icon: '📊' },
    { id: 'live-feed', label: 'Live Feed', component: LiveFeed, icon: '📡' },
    { id: 'daily-metrics', label: 'Daily Metrics', component: DailyMetrics, icon: '📈' },
    { id: 'enhancements', label: 'Enhancements', component: EnhancementDashboard, icon: '⚙️' },
    { id: 'health', label: 'Health Monitor', component: HealthMonitor, icon: '🏥' },
    { id: 'risk', label: 'Risk Analysis', component: RiskAnalysis, icon: '⚠️' },
    { id: 'trades', label: 'Trade Analysis', component: TradeAnalysis, icon: '🎯' },
    { id: 'reports', label: 'Reports', component: Reports, icon: '📋' },
  ]

  const activeTabObj = tabs.find(t => t.id === activeTab)
  const ActiveComponent = activeTabObj?.component || TradingValidationHome

  return (
    <div className="trading-validation-hub" style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: 'linear-gradient(135deg, #0f172a 0%, #1a2942 100%)',
      color: '#e0e7ff'
    }}>
      {/* Tab Navigation */}
      <div style={{
        display: 'flex',
        gap: '8px',
        padding: '16px 20px',
        borderBottom: '2px solid #334155',
        overflowX: 'auto',
        background: 'rgba(15, 23, 42, 0.8)',
        backdropFilter: 'blur(10px)'
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: activeTab === tab.id ? '2px solid #4f46e5' : '1px solid #334155',
              background: activeTab === tab.id ? 'rgba(79, 70, 229, 0.2)' : 'transparent',
              color: activeTab === tab.id ? '#a5f3fc' : '#a5b4fc',
              cursor: 'pointer',
              fontWeight: activeTab === tab.id ? '600' : '500',
              fontSize: '0.9rem',
              transition: 'all 0.3s ease',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseOver={(e) => {
              if (activeTab !== tab.id) {
                e.currentTarget.style.borderColor = '#4f46e5'
                e.currentTarget.style.color = '#c7d2fe'
              }
            }}
            onMouseOut={(e) => {
              if (activeTab !== tab.id) {
                e.currentTarget.style.borderColor = '#334155'
                e.currentTarget.style.color = '#a5b4fc'
              }
            }}
          >
            {tab.icon && <span>{tab.icon}</span>}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden'
      }}>
        <ActiveComponent />
      </div>
    </div>
  )
}

export default TradingValidationHub
